import { Table, Button, Alert, Spinner, Form } from "react-bootstrap";
import { useGetStoreSuppliesQuery } from "../../slices/storeSuppliesApiSlice";
import { useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const StoreSupplySummary = () => {
  const { data, isLoading, error } = useGetStoreSuppliesQuery();
  const [summary, setSummary] = useState({});
  const [showTable, setShowTable] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateError, setDateError] = useState("");

  const generateSummary = () => {
    if (startDate && endDate && endDate < startDate) {
      setDateError("End date must be after start date.");
      return;
    }

    setDateError("");

    const invoiceMap = {};

    data?.storeSupplies?.forEach((storeSupply) => {
      const storeSupplyDate = new Date(storeSupply.date).toISOString().split("T")[0];
      const withinRange =
        (!startDate || storeSupplyDate >= startDate) &&
        (!endDate || storeSupplyDate <= endDate);

      if (!withinRange) return;

      const invoice = storeSupply.note?.trim();
      const supplier = storeSupply.supplierDetails?.name?.trim() || "Unknown";

      if (!invoice) return;

      if (!invoiceMap[invoice]) {
        invoiceMap[invoice] = {
          qty: 0,
          cost: 0,
          profit: 0,
          supplier,
        };
      }

      storeSupply.storeSupplyItems.forEach((item) => {
        invoiceMap[invoice].qty += item.qty;
        invoiceMap[invoice].cost += item.itemCost;
        invoiceMap[invoice].profit += item.profit;
      });
    });

    setSummary(invoiceMap);
    setShowTable(true);
  };

  const exportToExcel = () => {
    const rows = Object.entries(summary).map(([invoice, entry]) => ({
      Invoice: invoice,
      Supplier: entry.supplier,
      Quantity: entry.qty,
      Cost: entry.cost,
      Profit: entry.profit,
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Invoice Summary");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "StoreSupply_Invoice_Summary.xlsx");
  };

  return (
    <div className="container mt-4">
      <h2 className="text-center mb-4">MiniSupply Invoice Summary</h2>

      <Form className="d-flex justify-content-center flex-wrap mb-3">
        <Form.Group className="mx-2">
          <Form.Label>Start Date:</Form.Label>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mx-2">
          <Form.Label>End Date:</Form.Label>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Form.Group>
        <div className="d-flex align-items-end mx-2">
          <Button variant="primary" onClick={generateSummary} disabled={isLoading}>
            {isLoading ? <Spinner animation="border" size="sm" /> : "Generate Summary"}
          </Button>
        </div>
      </Form>

      {dateError && (
        <Alert variant="danger" className="text-center">
          {dateError}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="text-center">
          {error?.data?.message || "Error loading storeSupplies."}
        </Alert>
      )}

      {showTable && (
        <>
          <Table striped bordered hover responsive>
            <thead className="text-center">
              <tr>
                <th>Invoice</th>
                <th>Supplier</th>
                <th>Total Quantity</th>
                <th>Total Cost</th>
                <th>Total Profit</th>
              </tr>
            </thead>
            <tbody className="text-center">
              {Object.entries(summary)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([invoice, entry]) => (
                  <tr key={invoice}>
                    <td>{invoice}</td>
                    <td>{entry.supplier}</td>
                    <td>{entry.qty}</td>
                    <td>${entry.cost.toFixed(2)}</td>
                    <td>${entry.profit.toFixed(2)}</td>
                  </tr>
                ))}
              <tr className="fw-bold">
                <td colSpan={2}>Total</td>
                <td>
                  {Object.values(summary).reduce((sum, entry) => sum + entry.qty, 0)}
                </td>
                <td>
                  $
                  {Object.values(summary)
                    .reduce((sum, entry) => sum + entry.cost, 0)
                    .toFixed(2)}
                </td>
                <td>
                  $
                  {Object.values(summary)
                    .reduce((sum, entry) => sum + entry.profit, 0)
                    .toFixed(2)}
                </td>
              </tr>
            </tbody>
          </Table>

          <div className="text-center mt-3">
            <Button variant="success" onClick={exportToExcel}>
              Export to Excel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default StoreSupplySummary;
